import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import {
  Box,
  Typography,
  Grid, 	
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  filterByDirection,
  sortConnections,
} from "../../utilities/connectionHelpers/connectionsHelpers";
import { PassengerModal } from "../SeatMapComponents/PassengerModal";
import ConnectionHeader from "./ConnectionHeader";
import PassengerList from "./PassengerList";

const TOGGLE_STORAGE_KEY = "CONNECTIONS_VIEW_FILTER";

const Toggle = ({
  mode,
  onChange,
  counts = { all: 0, inbound: 0, outbound: 0 },
}) => {
  return (
    <ToggleButtonGroup
      value={mode}
      exclusive
      onChange={(_, v) => v && onChange(v)}
    >
      <ToggleButton value={"ALL"}>All ({counts.all})</ToggleButton>
      <ToggleButton value={"INBOUND"}>Inbound ({counts.inbound})</ToggleButton>
      <ToggleButton value={"OUTBOUND"}>
        Outbound ({counts.outbound})
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

export const ConnectionsTab = ({
  connections = [],
  currentDestination,
  selectedFlight,
}) => {
  const [mode, setMode] = useState("ALL");
  const listRef = useRef(null);
  const [allMinHeight, setAllMinHeight] = useState(0);
  const [isPassengerModalOpen, setIsPassengerModalOpen] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState({});

  useEffect(() => {
    const saved = sessionStorage.getItem(TOGGLE_STORAGE_KEY);
    if (saved === "ALL" || saved === "INBOUND" || saved === "OUTBOUND") {
      setMode(saved);
    }
  }, []);

  // Persist toggle state
  useEffect(() => {
    sessionStorage.setItem(TOGGLE_STORAGE_KEY, mode);
  }, [mode]);

  // Filter connections by direction
  const filtered = useMemo(
    () => filterByDirection(connections, mode, currentDestination),
    [connections, mode, currentDestination]
  );

  // Sort connections by time
  const sorted = useMemo(
    () => sortConnections(filtered, currentDestination),
    [filtered, currentDestination]
  );

  // Count connections by direction
  const counts = useMemo(() => {
    const list = Array.isArray(connections) ? connections : [];
    const inbound = list.filter((c) => c.direction === "inbound").length;
    const outbound = list.filter((c) => c.direction === "outbound").length;
    return { all: list.length, inbound, outbound };
  }, [connections]);

  // Empty state
  const NoConnections = useCallback(() => {
    const text =
      mode === "INBOUND"
        ? "No inbound connections"
        : mode === "OUTBOUND"
        ? "No outbound connections"
        : "No connections";
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant={"body2"} color={"text.secondary"} align={"center"}>
          {text}
        </Typography>
      </Box>
    );
  }, [mode]);

  // Capture height of all connections
  useLayoutEffect(() => {
    try {
      if (mode === "ALL" && listRef.current) {
        const h = listRef.current.offsetHeight || 0;
        if (h > 0) setAllMinHeight(h);
      }
    } catch (error) {
      console.error("Error capturing height:", error);
    }
  }, [mode, sorted.length]);

  return (
    <Box sx={{ width: "100%" }}>
      <PassengerModal
        isOpen={!!isPassengerModalOpen}
        handleClose={() => setIsPassengerModalOpen(false)}
        selectedPassenger={selectedPassenger}
      />
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Toggle mode={mode} onChange={setMode} counts={counts} />
      </Box>

      {!sorted.length ? (
        <NoConnections />
      ) : (
        <Box
          ref={listRef}
          sx={{
            minHeight: allMinHeight
              ? `min(${allMinHeight}px, 50vh)`
              : undefined,
          }}
        >
          <Grid container sx={{ placeContent: "center" }}>
            <Typography variant="caption">
              All Connection times are in LT
            </Typography>
          </Grid>
          {sorted.map((c) => (
            <Accordion key={c.id} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <ConnectionHeader
                  connection={c}
                  selectedFlight={selectedFlight}
                />
              </AccordionSummary>
              <AccordionDetails>
                <PassengerList
                  passengers={c.passengers}
                  onSelectPassenger={(p) => {
                    setSelectedPassenger({ ...p });
                    setIsPassengerModalOpen(true);
                  }}
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ConnectionsTab;
